import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import Dialog from "../../../../../components/dialog/Dialog";
import DialogButton from "../../../../../components/dialog/DialogButton";
import SelectInput from "../../../../../components/inputs/selectInput/SelectInput";
import TextArea from "../../../../../components/inputs/textArea/TextArea";
import {
  fetchTablePlayersById,
  movePlayers,
} from "../../../../../features/players/playersSlice";
import styles from "./transferModal.module.css";

const normalizeValue = (value) => String(value || "").trim();
const normalizeToken = (value) => normalizeValue(value).toLowerCase();

const shouldUseContainsMatch = (token) =>
  /[a-zA-Z\u0600-\u06FF]/.test(token || "");

const matchesToken = (candidateValue, normalizedToken) => {
  const normalizedCandidate = normalizeToken(candidateValue);
  if (!normalizedCandidate || !normalizedToken) return false;

  if (normalizedCandidate === normalizedToken) return true;

  if (shouldUseContainsMatch(normalizedToken)) {
    return normalizedCandidate.includes(normalizedToken);
  }

  return false;
};

const buildEntryCandidates = (entry) => {
  if (!entry) return [];

  if (entry.kind === "squad") {
    const topLevel = [
      entry?.name,
      entry?.squadSlug,
      entry?.squadId,
      entry?._id,
    ];
    const members = Array.isArray(entry?.members) ? entry.members : [];
    const memberFields = members.flatMap((member) => [
      member?.gameId,
      member?.userId,
      member?._id,
      member?.userName,
      member?.username,
      member?.name,
      member?.gameName,
    ]);

    return [...topLevel, ...memberFields].filter(Boolean);
  }

  return [
    entry?.gameId,
    entry?.userId,
    entry?._id,
    entry?.name,
    entry?.gameName,
  ].filter(Boolean);
};

const getMatchedEntries = (entries = [], lines = []) => {
  const normalizedLines = [
    ...new Set((lines || []).map(normalizeToken).filter(Boolean)),
  ];
  if (!normalizedLines.length) return [];

  return (Array.isArray(entries) ? entries : []).filter((entry) => {
    const candidates = buildEntryCandidates(entry);
    if (!candidates.length) return false;

    return normalizedLines.some((token) =>
      candidates.some((candidate) => matchesToken(candidate, token)),
    );
  });
};

const isLineMatched = (entries = [], line = "") => {
  const normalizedLine = normalizeToken(line);
  if (!normalizedLine) return false;

  return (Array.isArray(entries) ? entries : []).some((entry) => {
    const candidates = buildEntryCandidates(entry);
    return candidates.some((candidate) =>
      matchesToken(candidate, normalizedLine),
    );
  });
};

const resolveLineToEntry = (entries = [], line = "") => {
  const normalizedLine = normalizeToken(line);
  if (!normalizedLine) return null;

  return (
    (Array.isArray(entries) ? entries : []).find((entry) => {
      const candidates = buildEntryCandidates(entry);
      return candidates.some((candidate) =>
        matchesToken(candidate, normalizedLine),
      );
    }) || null
  );
};

const TransferModal = ({ closeModalHandler, tournamentType = "solo" }) => {
  const [value, setValue] = useState("");
  const [ids, setIds] = useState([]);
  const [toTableId, setToTableId] = useState("");
  const [fromTableIds, setFromTableIds] = useState([]);
  const [fromTableSelectionValue, setFromTableSelectionValue] = useState("");

  const { list: tables } = useSelector((state) => state.tables);
  const playersByTableId = useSelector((state) => state.players.data || {});

  const dispatch = useDispatch();
  const { tournamentId } = useParams();

  const tableById = useMemo(
    () =>
      new Map(
        (Array.isArray(tables) ? tables : []).map((table) => [
          normalizeValue(table?._id),
          table,
        ]),
      ),
    [tables],
  );

  const sourceEntries = useMemo(
    () =>
      fromTableIds.flatMap((tableId) =>
        (Array.isArray(playersByTableId?.[tableId])
          ? playersByTableId[tableId]
          : []
        ).map((entry) => ({
          ...entry,
          fromTableId: normalizeValue(tableId),
        })),
      ),
    [fromTableIds, playersByTableId],
  );

  const selectedPlayers = useMemo(
    () => getMatchedEntries(sourceEntries, ids),
    [sourceEntries, ids],
  );

  const selectedFromTables = useMemo(
    () =>
      fromTableIds
        .map((tableId) => tableById.get(normalizeValue(tableId)))
        .filter(Boolean),
    [fromTableIds, tableById],
  );

  const allLinesMatched = ids.every((line) =>
    isLineMatched(sourceEntries, line),
  );

  const handleTransfer = () => {
    if (
      !fromTableIds.length ||
      !toTableId ||
      fromTableIds.includes(normalizeValue(toTableId)) ||
      !selectedPlayers.length ||
      !allLinesMatched
    )
      return;

    const entryMoves = selectedPlayers.map((entry) => {
      if (entry.kind === "squad") {
        return {
          kind: "squad",
          squadId: entry.squadId || entry._id,
          fromTableId: entry.fromTableId,
        };
      }

      return {
        kind: "user",
        userId: entry.userId || entry._id,
        fromTableId: entry.fromTableId,
      };
    });

    dispatch(movePlayers({ entryMoves, toTableId, tournamentId }));
  };

  const handleTextChange = (newText) => {
    setValue(newText);
    const updatedLines = newText
      .split(/\r?\n/)
      .map((line) => normalizeValue(line))
      .filter((line) => line.length > 0);

    setIds(updatedLines);
  };

  const handleRemoveLine = (target) => {
    const updatedLines = ids.filter((line) => line !== target);
    setIds(updatedLines);
    setValue(updatedLines.join("\n"));
  };

  const handleSelectFromTable = (nextTableId) => {
    const normalizedTableId = normalizeValue(nextTableId);
    setFromTableSelectionValue("");

    if (!normalizedTableId) return;

    setFromTableIds((prev) => {
      if (prev.includes(normalizedTableId)) return prev;
      return [...prev, normalizedTableId];
    });

    if (normalizeValue(toTableId) === normalizedTableId) {
      setToTableId("");
    }
  };

  const handleRemoveSourceTable = (targetTableId) => {
    const normalizedTargetTableId = normalizeValue(targetTableId);
    setFromTableIds((prev) =>
      prev.filter(
        (tableId) => normalizeValue(tableId) !== normalizedTargetTableId,
      ),
    );
  };

  const displayIds = ids.map((item, index) => {
    const matchedEntry = resolveLineToEntry(sourceEntries, item);
    const matchedTableName = matchedEntry?.fromTableId
      ? tableById.get(normalizeValue(matchedEntry.fromTableId))?.name || ""
      : "";
    const displayText = matchedEntry
      ? matchedEntry.kind === "squad"
        ? `سكواد: ${matchedEntry?.name || "-"}${
            matchedEntry?.squadSlug ? ` (${matchedEntry.squadSlug})` : ""
          }${matchedTableName ? ` - ${matchedTableName}` : ""}`
        : `${matchedEntry?.gameId || item}${
            matchedTableName ? ` - ${matchedTableName}` : ""
          }`
      : item;

    return (
      <div
        className={
          matchedEntry ? styles.oneId : `${styles.oneId} ${styles.redFlag}`
        }
        key={index}
      >
        <button
          type="button"
          className={styles.oneIdRemoveBtn}
          onClick={() => handleRemoveLine(item)}
          aria-label="remove entry"
        >
          <span aria-hidden="true" className={styles.removeGlyph}>
            x
          </span>
        </button>
        {displayText}
      </div>
    );
  });

  const displayToTableOptions = tables.map((table) => {
    const normalizedTableId = normalizeValue(table?._id);
    return (
      <option
        key={table._id}
        value={table._id}
        disabled={fromTableIds.includes(normalizedTableId)}
      >
        {table.name}
      </option>
    );
  });

  const displayFromTableOptions = tables.map((table) => {
    const normalizedTableId = normalizeValue(table?._id);
    return (
      <option
        key={table._id}
        value={table._id}
        disabled={
          fromTableIds.includes(normalizedTableId) ||
          normalizeValue(toTableId) === normalizedTableId
        }
      >
        {table.name}
      </option>
    );
  });

  const displaySelectedFromTables = selectedFromTables.map((table) => (
    <div className={styles.selectedSourceTableRow} key={table._id}>
      <button
        type="button"
        className={styles.selectedSourceTableRemoveBtn}
        onClick={() => handleRemoveSourceTable(table?._id)}
        aria-label="remove source table"
      >
        <span
          aria-hidden="true"
          className={`${styles.removeGlyph} ${styles.selectedSourceTableRemoveIcon}`}
        >
          x
        </span>
      </button>
      <span className={styles.selectedSourceTableName}>
        {table?.name || table?._id}
      </span>
    </div>
  ));

  useEffect(() => {
    const missingTableIds = fromTableIds.filter(
      (tableId) =>
        !Object.prototype.hasOwnProperty.call(
          playersByTableId,
          normalizeValue(tableId),
        ),
    );

    if (!missingTableIds.length) return;

    missingTableIds.forEach((tableId) => {
      dispatch(
        fetchTablePlayersById({
          tableId,
          tournamentId,
          silent: true,
        }),
      );
    });
  }, [dispatch, fromTableIds, playersByTableId, tournamentId]);

  return (
    <Dialog
      title={
        tournamentType === "squad"
          ? "نقل الفرق بين الجداول"
          : "نقل اللاعبين بين الجداول"
      }
      size="xl"
      onClose={closeModalHandler}
      // The two pickers were laid out for a left-to-right parent.
      bodyDir="ltr"
      footer={
        <>
          <DialogButton variant="secondary" onClick={closeModalHandler}>
            إغلاق
          </DialogButton>
          <DialogButton variant="primary" onClick={handleTransfer}>
            نقل العناصر
          </DialogButton>
        </>
      }
    >
      <div className={styles.container}>
        <div className={styles.modalHeaderWrapper}>
          <div className={styles.selectColumn}>
            <SelectInput
              wrapperClassName={styles.fullSelect}
              placeHolder={"يتم النقل الى"}
              value={toTableId}
              setValue={setToTableId}
            >
              {displayToTableOptions}
            </SelectInput>
          </div>
          <div className={styles.selectColumn}>
            <SelectInput
              wrapperClassName={styles.fullSelect}
              placeHolder={"يتم النقل من"}
              value={fromTableSelectionValue}
              setValue={handleSelectFromTable}
            >
              {displayFromTableOptions}
            </SelectInput>
            {displaySelectedFromTables.length > 0 && (
              <div className={styles.selectedSourceTablesWrapper}>
                {displaySelectedFromTables}
              </div>
            )}
          </div>
        </div>
        <div className={styles.textAreaWrapper}>
          <TextArea
            rows={20}
            placeHolder={
              tournamentType === "squad"
                ? "ايدي الاعضاء او اسم السكواد او معرف السكواد"
                : "ايدي اللاعبين"
            }
            value={value}
            onChange={(e) => handleTextChange(e.target.value)}
          />
        </div>
        <div className={styles.idsWrapper}>{displayIds}</div>
      </div>
    </Dialog>
  );
};

export default TransferModal;
